import React, { FC } from 'react';

import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import ReactMarkdown from 'react-markdown';

import { TimeZoneEnums } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';

import { ApiResponse } from '~/core/api';
import { Ratio, Size } from '~/core/forge/enums';
import { StoryByTag } from '~/core/forge/types';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { createForgeImageURL, getEnvironmentAwareHref } from '~/core/utils';

import { LOADING_DISPLAY_SIZE } from '../../styles';

import {
    Date,
    Description,
    GamePreviewContainer,
    InfoContainer,
    PreviewContainer,
    PreviewImage,
    Title,
} from './styles';

type Props = {
    gamePreviewAPIResponse: ApiResponse<StoryByTag>;
};

const formatDate = (dateString: string): string => {
    const date = DateTime.fromISO(dateString).setZone(TimeZoneEnums.AMERICA_NEW_YORK);
    return date.toLocaleString(DateTime.DATE_MED);
};

const GamePreview: FC<Props> = ({ gamePreviewAPIResponse }) => {
    const isWebview = useIsWebview();

    const { data, isLoading } = gamePreviewAPIResponse;

    if (isLoading) {
        return <LoadingDisplay isLoading={isLoading} isInline size={LOADING_DISPLAY_SIZE} />;
    }

    const item = data?.items?.[0];
    return item ? (
        <GamePreviewContainer>
            <Title role="heading" aria-level={2}>
                <FormattedMessage
                    description="A phrase to describe a preview of a game between two teams"
                    defaultMessage="Game Preview"
                />
            </Title>
            <PreviewContainer href={getEnvironmentAwareHref(`/news/${item.slug}`, isWebview)}>
                {item.thumbnail && (
                    <PreviewImage src={createForgeImageURL(item.thumbnail.thumbnailUrl, Ratio.SQUARE, Size.SIZE_10)} />
                )}
                <InfoContainer>
                    <Description>
                        <ReactMarkdown>{item.headline}</ReactMarkdown>
                    </Description>
                    <Date>{formatDate(item.contentDate)}</Date>
                </InfoContainer>
            </PreviewContainer>
        </GamePreviewContainer>
    ) : (
        <></>
    );
};

export default GamePreview;
